import * as THREE from 'three';
import { valueNoise2 } from '../core/rng';
import { PARAMS } from '../sim/params';
import { BIOME, WATER_LEVEL, groundBiome, terrainHeight } from '../sim/terrain';
import { GRID_N } from '../sim/stigmergy';

/**
 * Low-poly island mesh. The heightfield + biome map live in sim/terrain.ts
 * (pure math, seeded) so the worker, tests, and renderer all agree on the
 * same island. Faces are colored by biome; the mesh follows the height field,
 * so raised biome patches sit proud of the grass automatically.
 *
 * Build the Terrain AFTER configureTerrain(seed) so it captures this seed's
 * coastline, ponds, and biomes.
 */
const SEGMENTS = 112; // fine enough for streams (~1.2u) and the ragged coast

// per-biome palettes: dark/light base blended by smooth noise (soft patches,
// not a checkerboard), plus a sparse accent sprinkled per-face for texture
// (flower blooms, mossy rocks, dirt clods)
const PALETTES: Record<number, { dark: string; light: string; accent?: string; accentP?: number }> = {
  [BIOME.GRASS]: { dark: '#43893A', light: '#5FAE4A' },
  [BIOME.FLOWER]: { dark: '#4C9A3D', light: '#5EAE49', accent: '#E86FA6', accentP: 0.12 },
  [BIOME.DIRT]: { dark: '#7C5D34', light: '#957043', accent: '#6B4F2C', accentP: 0.12 },
  [BIOME.MOSS]: { dark: '#4F6B3C', light: '#5C7A46', accent: '#8C8F80', accentP: 0.14 },
  [BIOME.SAND]: { dark: '#CFBE7C', light: '#E1D298' },
  [BIOME.MUD]: { dark: '#5E4C31', light: '#6E5A3C' },
};
const FLOWER_COLORS = ['#E86FA6', '#F2D84C', '#EF8F5A', '#B79BE0', '#F5EFE0'];

function faceRand(x: number, z: number, salt: number): number {
  let h = Math.imul(((x * 8) | 0) + 1, 0x27d4eb2d) ^ Math.imul(((z * 8) | 0) + 1, 0x165667b1) ^ salt;
  h = Math.imul(h ^ (h >>> 15), 0x85ebca6b);
  return ((h ^ (h >>> 13)) >>> 0) / 4294967296;
}

export class Terrain {
  readonly mesh: THREE.Mesh;
  private readonly groundData = new Uint8Array(GRID_N * GRID_N * 4);
  private readonly groundTex: THREE.DataTexture;

  constructor(pixelMap: THREE.Texture | null = null) {
    this.groundTex = new THREE.DataTexture(this.groundData, GRID_N, GRID_N, THREE.RGBAFormat);
    this.groundTex.magFilter = THREE.LinearFilter;
    this.groundTex.minFilter = THREE.LinearFilter;
    this.groundTex.flipY = false;
    this.groundTex.needsUpdate = true;

    let geo: THREE.BufferGeometry = new THREE.PlaneGeometry(
      PARAMS.WORLD_SIZE * 1.5,
      PARAMS.WORLD_SIZE * 1.5,
      SEGMENTS,
      SEGMENTS,
    );
    geo.rotateX(-Math.PI / 2);

    const pos = geo.getAttribute('position') as THREE.BufferAttribute;
    for (let i = 0; i < pos.count; i++) {
      pos.setY(i, terrainHeight(pos.getX(i), pos.getZ(i)));
    }

    // non-indexed + per-face color + flat normals = the low-poly patchwork
    geo = geo.toNonIndexed();
    geo.computeVertexNormals();
    const p = geo.getAttribute('position') as THREE.BufferAttribute;
    const colors = new Float32Array(p.count * 3);
    const c = new THREE.Color();
    const TMP_LIGHT = new THREE.Color();
    for (let f = 0; f < p.count / 3; f++) {
      const i0 = f * 3;
      const cx = (p.getX(i0) + p.getX(i0 + 1) + p.getX(i0 + 2)) / 3;
      const cz = (p.getZ(i0) + p.getZ(i0 + 1) + p.getZ(i0 + 2)) / 3;

      const biome = groundBiome(cx, cz);
      const pal = PALETTES[biome] ?? PALETTES[BIOME.GRASS]!;
      const rnd = faceRand(cx, cz, 3);
      if (pal.accent && rnd < (pal.accentP ?? 0)) {
        if (biome === BIOME.FLOWER) {
          c.set(FLOWER_COLORS[(faceRand(cx, cz, 9) * FLOWER_COLORS.length) | 0] as string);
        } else {
          c.set(pal.accent);
        }
      } else {
        // smooth low-freq blend between the biome's dark & light base
        const t = valueNoise2(cx * 0.28, cz * 0.28, 7);
        c.set(pal.dark).lerp(TMP_LIGHT.set(pal.light), t);
      }
      // faint per-face brightness grain
      const j = 0.96 + faceRand(cx, cz, 21) * 0.08;
      for (let v = 0; v < 3; v++) {
        colors[(i0 + v) * 3] = c.r * j;
        colors[(i0 + v) * 3 + 1] = c.g * j;
        colors[(i0 + v) * 3 + 2] = c.b * j;
      }
    }
    geo.setAttribute('color', new THREE.BufferAttribute(colors, 3));

    const material = new THREE.MeshLambertMaterial({ vertexColors: true });
    if (pixelMap) {
      pixelMap.repeat.set(72, 72);
      material.map = pixelMap; // adds pixel grain; vertex colors carry the biome
    }
    const groundTex = this.groundTex;
    const waterLine = WATER_LEVEL.toFixed(3);
    material.onBeforeCompile = (shader) => {
      shader.uniforms.uGround = { value: groundTex };
      shader.vertexShader =
        'varying vec2 vWXZ;\nvarying float vWY;\n' +
        shader.vertexShader.replace(
          '#include <begin_vertex>',
          '#include <begin_vertex>\nvWXZ = position.xz;\nvWY = position.y;',
        );
      shader.fragmentShader =
        'uniform sampler2D uGround;\nvarying vec2 vWXZ;\nvarying float vWY;\n' +
        shader.fragmentShader.replace(
          '#include <color_fragment>',
          /* glsl */ `#include <color_fragment>
          {
            vec2 guv = (vWXZ + ${(PARAMS.WORLD_SIZE / 2).toFixed(1)}) / ${PARAMS.WORLD_SIZE.toFixed(1)};
            float inb = step(0.0, guv.x) * step(guv.x, 1.0) * step(0.0, guv.y) * step(guv.y, 1.0);
            vec2 ground = texture2D(uGround, guv).rg * inb;
            // worn desire paths: grass gives way to packed earth
            diffuseColor.rgb = mix(diffuseColor.rgb, vec3(0.55, 0.45, 0.26), min(0.85, ground.r));
            // joyful ground: a faint warm flush where they play
            diffuseColor.rgb += ground.g * vec3(0.10, 0.05, 0.0);
            // wet/foam band wherever land meets the waterline (coast + ponds)
            float shore = smoothstep(0.22, 0.02, abs(vWY - (${waterLine})));
            vec2 cell = floor(vWXZ * 1.5);
            float fn = fract(sin(dot(cell, vec2(12.9898, 78.233))) * 43758.5453);
            diffuseColor.rgb = mix(diffuseColor.rgb, vec3(0.88, 0.93, 0.95), shore * step(0.48, fn) * 0.8);
          }`,
        );
    };
    material.customProgramCacheKey = () => 'terrain-ground';

    this.mesh = new THREE.Mesh(geo, material);
    this.mesh.name = 'terrain';
  }

  heightAt(x: number, z: number): number {
    return terrainHeight(x, z);
  }

  /** Paint the stigmergy render grid (2 bytes/cell: wear, joy) into the texture. */
  updateGround(grid: Uint8Array): void {
    const n = GRID_N * GRID_N;
    for (let i = 0; i < n; i++) {
      this.groundData[i * 4] = grid[i * 2] as number;
      this.groundData[i * 4 + 1] = grid[i * 2 + 1] as number;
    }
    this.groundTex.needsUpdate = true;
  }
}

/**
 * One translucent water plane at the waterline. It's visible wherever the
 * terrain dips below it — the open sea beyond the coast, and every pond and
 * stream carved inland — and occluded by land everywhere else.
 */
export function createWater(pixelMap: THREE.Texture | null = null): THREE.Mesh {
  const material = new THREE.MeshLambertMaterial({
    color: '#4E86C4',
    transparent: true,
    opacity: 0.82,
    depthWrite: false,
  });
  if (pixelMap) {
    pixelMap.repeat.set(60, 60);
    material.map = pixelMap; // caller scrolls pixelMap.offset for drift
  }
  const mesh = new THREE.Mesh(
    new THREE.PlaneGeometry(PARAMS.WORLD_SIZE * 5, PARAMS.WORLD_SIZE * 5),
    material,
  );
  mesh.rotation.x = -Math.PI / 2;
  mesh.position.y = WATER_LEVEL;
  mesh.renderOrder = 1; // drawn after the opaque terrain
  mesh.name = 'water';
  return mesh;
}
